export const SINGLE_FIELDS = [
  'gender',
  'age_group',
  'transport_method',
  'visit_frequency',
  'companion',
  'visit_purpose_main',
  'visit_budget',
  'office_awareness',
  'satisfaction',
] as const;

export const MULTI_FIELDS = [
  'favorite_shops',
  'sns_usage',
  'facility_awareness',
  'desired_facilities',
  'desired_events',
  'awareness_source',
  'campaign_source',
] as const;

export type SingleField = (typeof SINGLE_FIELDS)[number];
export type MultiField = (typeof MULTI_FIELDS)[number];
export type CodebookField = SingleField | MultiField | 'day';

export type Codebook = Record<CodebookField, string[]>;

export interface EncodedResponse {
  id: number;
  gender: number;
  age_group: number;
  transport_method: number;
  visit_frequency: number;
  companion: number;
  visit_purpose_main: number;
  visit_budget: number;
  office_awareness: number;
  satisfaction: number;
  favorite_shops: number[];
  sns_usage: number[];
  facility_awareness: number[];
  desired_facilities: number[];
  desired_events: number[];
  awareness_source: number[];
  campaign_source: number[];
  postal: string | null;
  day: number;
  hour: number;
}

export interface ResponsesArtifact {
  rows: EncodedResponse[];
  codebook: Codebook;
}

export interface CrosstabCell {
  n: number;
  small: boolean;
}

export interface AnnotatedCrosstab {
  cells: Record<string, Record<string, CrosstabCell>>;
  rowTotals: Record<string, number>;
  colTotals: Record<string, number>;
  total: number;
}

export interface PeriodCoverage {
  n: number;
  days: string[];
}

export type StoryStage = 'awareness' | 'purpose' | 'satisfaction';

export interface StoryMetric {
  n: number;
  rate: number;
}

export interface StorySankeyNode {
  id: string;
  label: string;
  stage: StoryStage;
  value: number;
}

export interface StorySankeyLink {
  source: string;
  target: string;
  value: number;
}

export interface MunicipalityCount {
  pref: string;
  city: string;
  n: number;
}

/** A count is only published when its postal prefix was eligible for mapping. */
export interface PrefectureCount {
  pref: string;
  n: number;
}

export interface LocalComparison {
  localPrefix: '559';
  localN: number;
  localLowN: number;
  localLowRate: number;
  outsideN: number;
  outsideLowN: number;
  outsideLowRate: number;
}

export interface EpisodeSummary {
  askedN: number;
  usableN: number;
  longN: number;
}

/** Every story assertion is generated with its provenance instead of being typed into React. */
export type EvidenceSource = 'coded-response' | 'reviewed-voice-theme' | 'brand-document' | 'editorial-synthesis';

export interface DashboardHandoff {
  tab: 'audience' | 'visit' | 'satisfaction' | 'awareness' | 'needs';
  /** Human-readable reproduction recipe. Filter values stay in the URL state. */
  label: string;
}

export interface EvidenceClaim {
  id: string;
  label: string;
  value: string;
  unit: 'people' | 'percent' | 'score' | 'voices' | 'statement';
  numerator: number | null;
  denominator: number | null;
  source: EvidenceSource;
  caveat: string | null;
  small: boolean;
  handoff: DashboardHandoff | null;
}

export interface ReviewedVoiceTheme {
  id: string;
  label: string;
  n: number;
  denominator: number;
  definition: string;
}

export interface DataStorySlide {
  id: 'event-conversion' | 'solo-experience' | 'brand-yohaku';
  marker: string;
  question: string;
  title: string;
  conclusion: string;
  evidenceIds: string[];
  dashboardTab: DashboardHandoff['tab'];
}

export interface MethodologyArtifact {
  title: string;
  madeBy: string;
  collection: { responses: number; days: number; context: string };
  limitations: string[];
  privacy: string[];
  updatedAt: string;
}

export interface DownloadArtifact {
  href: string;
  label: string;
  rows: number;
  sanitized: true;
  updatedAt: string;
}

export interface StoryAggregates {
  headlines: {
    family: StoryMetric;
    eventPurpose: StoryMetric;
    lightVisitors: StoryMetric;
    female: StoryMetric;
    train: StoryMetric;
    positiveSatisfaction: StoryMetric;
    verySatisfied: StoryMetric;
    satisfied: StoryMetric;
    satisfactionAverage: number;
    firstVisitPositiveRate: number;
    officeFirst: StoryMetric;
    firstVisitOfficeFirstRate: number;
    facilityNone: StoryMetric;
  };
  sankey: {
    note: '認知経路は複数回答のため按分';
    nodes: StorySankeyNode[];
    links: StorySankeyLink[];
  };
  geography: {
    validPostalN: number;
    prefectures: PrefectureCount[];
    municipalities: MunicipalityCount[];
    localComparison: LocalComparison;
  };
  facilityAwareness: Array<{ label: string; n: number }>;
  desiredFacilities: Array<{ label: string; n: number }>;
  heatComplaintCount: number;
  episodes: EpisodeSummary;
  evidence: EvidenceClaim[];
  negativeThemes: ReviewedVoiceTheme[];
  positiveThemes: ReviewedVoiceTheme[];
  dataStories: DataStorySlide[];
}

export interface AggregatesArtifact {
  total: number;
  gender: Record<string, number>;
  age_group: Record<string, number>;
  transport_method: Record<string, number>;
  visit_frequency: Record<string, number>;
  companion: Record<string, number>;
  visit_purpose_main: Record<string, number>;
  visit_budget: Record<string, number>;
  office_awareness: Record<string, number>;
  satisfaction: Record<string, number>;
  facility_awareness: Record<string, number>;
  desired_facilities: Record<string, number>;
  desired_events: Record<string, number>;
  sns_usage: Record<string, number>;
  favorite_shops: Record<string, number>;
  awareness_source: Record<string, number>;
  campaign_source: Record<string, number>;
  daily: Record<string, number>;
  hourly: Record<string, number>;
  crosstabs: {
    age_satisfaction: AnnotatedCrosstab;
    frequency_satisfaction: AnnotatedCrosstab;
    companion_satisfaction: AnnotatedCrosstab;
    transport_satisfaction: AnnotatedCrosstab;
  };
  smallNThreshold: number;
  geography: {
    requiredPrefixCount: number;
    observedPrefixCount: number;
  };
  periodLimited: {
    visit_budget: PeriodCoverage;
    atc_episode: PeriodCoverage;
  };
  story: StoryAggregates;
}

export type VoiceSource = 'atc_image' | 'atc_episode' | 'comments';
export type Sentiment = 'positive' | 'negative' | 'neutral';

export interface Voice {
  id: string;
  source: VoiceSource;
  text: string;
  length: number;
  themes: string[];
  sentiment: Sentiment;
  isRequest: boolean;
  heroCandidate: boolean;
  gender: string;
  age: string;
  satisfaction: string;
  frequency: string;
  postal: string | null;
  day: string;
}

export type KansaiPrefectureCode = '25' | '26' | '27' | '28' | '29' | '30';
export type KansaiPrefectureName = '滋賀県' | '京都府' | '大阪府' | '兵庫県' | '奈良県' | '和歌山県';
export type GeoPosition = [longitude: number, latitude: number];
export type GeoLinearRing = GeoPosition[];
export type GeoPolygonCoordinates = GeoLinearRing[];
export type GeoMultiPolygonCoordinates = GeoPolygonCoordinates[];

export interface KansaiMapSource {
  title: string;
  url: string;
  date: string;
  license: 'CC BY 4.0';
  attribution: string;
  modified: string;
}

export interface KansaiMunicipalityProperties {
  prefectureCode: KansaiPrefectureCode;
  prefectureName: KansaiPrefectureName;
  municipalityCode: string;
  municipalityName: string;
}

export interface KansaiMunicipalityFeature {
  type: 'Feature';
  id: string;
  properties: KansaiMunicipalityProperties;
  geometry: {
    type: 'MultiPolygon';
    coordinates: GeoMultiPolygonCoordinates;
  };
}

export interface KansaiMapArtifact {
  type: 'FeatureCollection';
  name: string;
  source: KansaiMapSource;
  bbox: [west: number, south: number, east: number, north: number];
  features: KansaiMunicipalityFeature[];
}

export interface JapanPrefectureProperties {
  prefectureCode: string;
  prefectureName: string;
}

export interface JapanPrefectureFeature {
  type: 'Feature';
  id: string;
  properties: JapanPrefectureProperties;
  geometry: {
    type: 'MultiPolygon';
    coordinates: GeoMultiPolygonCoordinates;
  };
}

/** Presentation-simplified derivative of MLIT N03 2026; never fetched at runtime. */
export interface JapanMapArtifact {
  type: 'FeatureCollection';
  name: string;
  source: KansaiMapSource;
  bbox: [west: number, south: number, east: number, north: number];
  features: JapanPrefectureFeature[];
}
